import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Avatar,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Payment as PaymentIcon,
  Badge as BadgeIcon,
  PersonPin as PersonPinIcon,
  Add as AddIcon,
  LocationOff as LocationOffIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { usersAPI,API_BASE_URL } from '../../services/api';
import { staffAPI } from '../../services/staffMember';
import { StaffMember } from '../../services/staffMember';
// Import the User interface from the API
import { User } from '../../services/api';


// Customer type definition for our component
interface Booking {
  booking_id: number;
  booking_number: string;
  user_id: number;
  vehicle_id: number;
  address_id: number;
  booking_date: string;
  slot_id: number;
  start_time: string;
  end_time: string;
  total_amount: string;
  discount_amount: string;
  tax_amount: string;
  tip_amount: number;
  final_amount: string;
  service_charges: string;
  addon_total: string;
  payment_status: string;
  payment_method: string;
  payment_id: string;
  status: string;
  cancellation_reason: string | null;
  refund_status: string | null;
  refund_amount: number | null;
  refund_id: string | null;
  assigned_staff_id: number;
  notes: string;
  contact_person_name: string;
  contact_person_number: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  cancelled_at: string | null;
  booking_otp: number;
  reschedule_status: number;
  commission_amount?: string;
  commission_status?: string;
  commission_paid_date?: string | null;
}

interface StaffDetails {
  sp_name: string;
  sp_email: string;
  sp_mobile: string;
  sp_status: 'active' | 'inactive' | 'suspended';
  sp_profile_photo: string | null;
  email_verified: number;
  mobile_verified: number;
  notification_enabled: number;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  bookings: Booking[];
  total_earnings?: string;
  total_commission?: string;
  pending_commission?: string;
  paid_commission?: string;
}


interface Payout {
  payout_id: number;
  staff_id: number;
  total_commission: string;
  payout_amount: string;
  payment_method: string;
  payment_reference: string;
  payout_status: string;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
}

interface PayoutsData {
  staff_id: string;
  total_earning: number;
  total_paid: number;
  pending_payment: number;
  payout_count: number;
  payouts: Payout[];
}

interface StaffCommissionReport {
  staff_id: string;
  total_commission_amount: number;
  commission_count: number;
  total_earning: number;
  total_paid: number;
  pending_payment: number;
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

interface TableTabPanelProps extends TabPanelProps {
  columns: { key: string; label: string; align?: 'left' | 'center' | 'right' }[];
  data: any[];
  renderCell?: (item: any, column: string) => React.ReactNode;
  emptyMessage?: string;
}

const TableTabPanel = (props: TableTabPanelProps) => {
  const { 
    value, 
    index, 
    columns, 
    data, 
    renderCell, 
    emptyMessage = 'No data available',
    ...other 
  } = props;

  return (
    <TabPanel value={value} index={index} {...other}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell 
                  key={column.key} 
                  align={column.align || 'left'}
                  sx={{ fontWeight: 'bold' }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((item, idx) => (
                <TableRow key={idx} hover>
                  {columns.map((column) => (
                    <TableCell key={`${idx}-${column.key}`} align={column.align || 'left'}>
                      {renderCell ? renderCell(item, column.key) : item[column.key]}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <IconButton size="small" color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanel>
  );
};

function a11yProps(index: number) {
  return {
    id: `customer-tab-${index}`,
    'aria-controls': `customer-tabpanel-${index}`,
  };
}

interface CommissionSettings {
  commission_type: 'percentage' | 'fixed';
  commission_value: number;
  min_amount?: number;
  max_amount?: number;
}

const StaffDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState(0);
  const [washer, setWasher] = useState<StaffDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commissionSettings, setCommissionSettings] = useState<CommissionSettings | null>(null);
  const [payoutsData, setPayoutsData] = useState<PayoutsData | null>(null);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsError, setPayoutsError] = useState<string | null>(null);
  const [addPayoutOpen, setAddPayoutOpen] = useState(false);
  const [addPayoutLoading, setAddPayoutLoading] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Online');
  const [paymentReference, setPaymentReference] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [commissionReport, setCommissionReport] = useState<StaffCommissionReport | null>(null);
  const ucfirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  const fetchPayouts = async () => {
    if (!id) return;

    try {
      setPayoutsLoading(true);
      setPayoutsError(null);

      const resp = await staffAPI.getPayouts(parseInt(id, 10));
      setPayoutsData(resp.data);
    } catch (err) {
      console.error('Error fetching payouts:', err);
      setPayoutsError('Failed to load payout data');
    } finally {
      setPayoutsLoading(false);
    }
  };

  const fetchCommissionReport = async () => {
    if (!id) return;

    try {
      const response = await axios.post(`${API_BASE_URL}/bookings/getStaffCommissionReport`, {
        staff_id: parseInt(id, 10),
      });

      const data = response?.data?.data;
      if (data) {
        setCommissionReport({
          staff_id: data.staff_id,
          total_commission_amount: data.total_commission_amount ?? 0,
          commission_count: data.commission_count ?? 0,
          total_earning: data.total_earning ?? 0,
          total_paid: data.total_paid ?? 0,
          pending_payment: data.pending_payment ?? 0,
        });
      }
    } catch (err) {
      console.error('Error fetching staff commission report:', err);
      setCommissionReport(null);
    }
  };

  useEffect(() => {
    const fetchCommissionSettings = async () => {
      try {
        const response = await axios.get('/api/settings/commission');
        setCommissionSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch commission settings:', error);
        // Set default commission settings if API fails
        setCommissionSettings({
          commission_type: 'percentage',
          commission_value: 10, // 10% default commission
          min_amount: 0,
          max_amount: 1000
        });
      }
    };

    const fetchStaffDetails = async () => {
      if (!id) {
        setError('Washer ID is required');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching washer details for ID:', id);
        
        // Fetch user data from the API with proper typing
        const response = await staffAPI.getById(parseInt(id));
        console.log('API Response:', response);
        
        if (!response) {
          throw new Error('No response from server');
        }
        
        // The API might be returning the user data in a data property
        const userData = (response as any)?.data || response;
        console.log('Washer Data:', userData);
        
        if (!userData) {
          throw new Error('Washer not found');
        }
        
        // Calculate commission for each booking if we have settings
        const bookings = userData.bookings?.map((booking: any) => ({
          ...booking
        })) || [];
        
        // Transform the API response to match our StaffDetails interface
        const StaffMember: StaffDetails = {
          sp_name: userData.sp_name || '',
          sp_email: userData.sp_email || '',
          sp_mobile: userData.sp_mobile || '',
          sp_status: userData.sp_status || 'active',
          sp_profile_photo: userData.profile_photo,
          email_verified: userData.email_verified || 0,
          mobile_verified: userData.mobile_verified || 0,
          notification_enabled: userData.notification_enabled || 0,
          created_at: userData.created_at || new Date().toISOString(),
          updated_at: userData.updated_at || new Date().toISOString(),
          last_login_at: userData.last_login_at || null,
          bookings: bookings,
          total_earnings: bookings.reduce((sum: number, b: any) => sum + parseFloat(b.final_amount || 0), 0).toFixed(2)
        };
        
        setWasher(StaffMember);
        setError(null);
      } catch (err) {
        console.error('Error in fetchWasherDetails:', {
          error: err,
          message: err instanceof Error ? err.message : 'Unknown error',
          stack: err instanceof Error ? err.stack : undefined
        });
        setError(`Error: ${err instanceof Error ? err.message : 'Failed to load customer details'}`);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCommissionSettings();
      fetchStaffDetails();
      fetchPayouts();
      fetchCommissionReport();
    }
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenAddPayout = () => {
    setPayoutAmount('');
    setPaymentMethod('Online');
    setPaymentReference('');
    setPayoutNotes('');
    setAddPayoutOpen(true);
  };

  const handleCloseAddPayout = () => {
    if (addPayoutLoading) return;
    setAddPayoutOpen(false);
  };

  const handleSubmitAddPayout = async () => {
    if (!id) return;

    try {
      setAddPayoutLoading(true);
      await axios.post(`${API_BASE_URL}/bookings/createStaffPayout`, {
        staff_id: parseInt(id, 10),
        payout_amount: parseFloat(payoutAmount || '0'),
        payment_method: paymentMethod,
        payment_reference: paymentReference,
        notes: payoutNotes,
      });

      setAddPayoutOpen(false);
      await fetchPayouts();
    } catch (err) {
      console.error('Error creating staff payout:', err);
    } finally {
      setAddPayoutLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color based on status
  const getStatusColor = (status: string) => {
    const statusMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
      'completed': 'success',
      'confirmed': 'info',
      'cancelled': 'error',
      'pending': 'warning',
    };
    return statusMap[status.toLowerCase()] || 'default';
  };

  const calculateCommission = (booking: any, commissionSettings: CommissionSettings) => {
    const serviceAmount = parseFloat(booking.final_amount) || 0;
    let commission = 0;
    
    if (commissionSettings.commission_type === 'percentage') {
      commission = serviceAmount * (commissionSettings.commission_value / 100);
    } else {
      commission = commissionSettings.commission_value;
    }
    
    // Apply min/max limits if they exist
    if (commissionSettings.min_amount !== undefined) {
      commission = Math.max(commission, commissionSettings.min_amount);
    }
    if (commissionSettings.max_amount !== undefined) {
      commission = Math.min(commission, commissionSettings.max_amount);
    }
    
    return commission.toFixed(2);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !washer) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Washer not found'}
        </Alert>
        <Button
          component={Link}
          to="/washers"
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Back to Washer
        </Button>
      </Box>
    );
  }


  if (!washer) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={Link}
          to="/washers"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          
        </Button>
        <Typography variant="h4" gutterBottom>
                    Washer Details
                </Typography>

      </Box>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Avatar
            src={washer.sp_profile_photo || undefined}
            sx={{ width: 80, height: 80, mr: 3 }}
          >
            {washer.sp_name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {washer.sp_name}
              <Chip
                label={washer.sp_status}
                color={washer.sp_status === 'active' ? 'success' : 'default'}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
            </Typography>
           
            <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {washer.sp_email}
                </Typography>
                {/*washer.email_verified ? (
                  <Chip
                    label="Verified"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ ml: 1, height: 20 }}
                  />
                ) : null*/}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {washer.sp_mobile}
                </Typography>
                {/*washer.mobile_verified ? (
                  <Chip
                    label="Verified"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ ml: 1, height: 20 }}
                  />
                ) : null*/}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: { xs: 2, sm: 0 } }}>
            
            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <span>Bookings:</span>
              {washer.bookings?.length > 0 ? (
                <Chip
                  label={washer.bookings.length}
                  size="small"
                  sx={{ ml: 1 }}
                />
              ) : (
                <Chip label="0" size="small" sx={{ ml: 1 }} variant="outlined" />
              )}
            </Box>
            {commissionReport && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                  <span>Total earnings:</span>
                  <Chip
                    label={`${process.env.REACT_APP_SITE_CURRENCY}${commissionReport.total_earning}`}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                  <span>Total paid:</span>
                  <Chip
                    label={`${process.env.REACT_APP_SITE_CURRENCY}${commissionReport.total_paid}`}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                  <span>Pending payment:</span>
                  <Chip
                    label={`${process.env.REACT_APP_SITE_CURRENCY}${commissionReport.pending_payment}`}
                    size="small"
                    sx={{ ml: 1 }}
                    color={commissionReport.pending_payment > 0 ? 'warning' : 'default'}
                    variant={commissionReport.pending_payment > 0 ? 'filled' : 'outlined'}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Washer details tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Basic Info" {...a11yProps(0)} />
            <Tab label="Bookings" {...a11yProps(1)} />
            <Tab label="Commissions" {...a11yProps(2)} />
            <Tab label="Payouts" {...a11yProps(3)} />
          </Tabs>
        </Box>

        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Personal Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Full Name" 
                        secondary={washer.sp_name} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Email" 
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {washer.sp_email}
                            {/*washer.email_verified && (
                              <Chip
                                label="Verified"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )*/}
                          </Box>
                        } 
                        secondaryTypographyProps={{ 
                          sx: { 
                            textOverflow: 'ellipsis', 
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center'
                          } 
                        }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Mobile" 
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {washer.sp_mobile}
                            {/*washer.mobile_verified && (
                              <Chip
                                label="Verified"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )*/}
                          </Box>
                        } 
                      />
                    </ListItem>
                   
                    <ListItem>
                      <ListItemIcon><PersonPinIcon /></ListItemIcon>
                      <ListItemText 
                        primary="Account Status"
                        secondary={
                          <Chip
                            label={washer.sp_status && ucfirst(washer.sp_status)}
                            color={washer.sp_status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          
          {washer.bookings.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {washer.bookings.map((booking) => (
                    <TableRow key={booking.booking_id}>
                      <TableCell>{booking.booking_number}</TableCell>
                      <TableCell>{new Date(booking.booking_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip 
                          label={booking.status} 
                          color={getStatusColor(booking.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{parseFloat(booking.final_amount).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          component={Link}
                          to={`/bookings/${booking.booking_id}`}
                          color="primary"
                          size="small"
                          title="View Booking"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No bookings found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This washer hasn't made any bookings yet.
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Calculate commission based on settings */}
        {commissionSettings && (
          <TabPanel value={tabValue} index={2}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Service Amount</TableCell>
                    <TableCell>Commission</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {washer.bookings && washer.bookings.length > 0 ? (
                    washer.bookings.map((booking) => (
                      <TableRow key={`comm-${booking.booking_id}`}>
                        <TableCell>{booking.booking_number}</TableCell>
                        <TableCell>{new Date(booking.booking_date).toLocaleDateString()}</TableCell>
                        <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{parseFloat(booking.final_amount).toFixed(2)}</TableCell>
                        <TableCell>{process.env.REACT_APP_SITE_CURRENCY}{booking.commission_amount || '0.00'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <ReceiptIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No commission records found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            This staff member doesn't have any commission records yet.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        )}

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mt: 2 }}>
            
            {payoutsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : payoutsError ? (
              <Alert severity="error">{payoutsError}</Alert>
            ) : payoutsData ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Payout History
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
              
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAddPayout}
              >
                Add Payout
              </Button>
            </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payoutsData.payouts.length > 0 ? (
                        payoutsData.payouts.map((payout) => (
                          <TableRow key={payout.payout_id}>
                            <TableCell>
                              {new Date(payout.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>{payout.payment_reference}</TableCell>
                            <TableCell align="right">
                              {process.env.REACT_APP_SITE_CURRENCY}{payout.payout_amount}
                            </TableCell>
                            <TableCell>{payout.payment_method}</TableCell>
                            <TableCell>
                              <Chip
                                label={payout.payout_status}
                                color={payout.payout_status === 'paid' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{payout.notes || '-'}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No payout history found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Typography>No payout data available</Typography>
            )}
          </Box>
        </TabPanel>
      </Paper>

      <Dialog open={addPayoutOpen} onClose={handleCloseAddPayout} fullWidth maxWidth="sm">
        <DialogTitle>Add Payout</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payout Amount"
                type="number"
                fullWidth
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Method"
                select
                fullWidth
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Payment Reference"
                fullWidth
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={2}
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddPayout} disabled={addPayoutLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAddPayout}
            disabled={addPayoutLoading || !payoutAmount}
          >
            {addPayoutLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffDetails;
